import React, { useState, useEffect } from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  faCheck,
  faCrown,
  faRocket,
  faStar,
  faArrowRight,
  faUsers,
  faMobile,
  faClock,
  faChartLine,
  faBrain,
  faShieldAlt,
  faSpinner
} from '@fortawesome/free-solid-svg-icons';
import PreRegistrationForm from './PreRegistrationForm';
import { GradientText } from '../ui/gradient-text';
import { useNavigate, useParams } from 'react-router-dom';
import { subscriptionAPI } from '../../services/api';
import { Package } from '../../types/models';
import { useTranslation } from 'react-i18next';

const Pricing: React.FC = () => {
  const navigate = useNavigate();
  const { lang } = useParams<{ lang: string }>();
  // @ts-ignore
  const { t } = useTranslation('landing');
  const [packages, setPackages] = useState<Package[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [selectedInterval, setSelectedInterval] = useState<'MONTHLY' | 'YEARLY'>('MONTHLY');

  useEffect(() => {
    const fetchPackages = async () => {
      try {
        setLoading(true);
        const response = await subscriptionAPI.getPackages();
        const packagesData = Array.isArray(response.data) ? response.data : response.data?.data || [];
        // Filter only active packages
        const activePackages = packagesData.filter((pkg: Package) => pkg.isActive);
        setPackages(activePackages);
      } catch (err: any) {
        console.error('Error fetching packages:', err);
        setError(t('pricing.error'));
      } finally {
        setLoading(false);
      }
    };

    fetchPackages();
  }, [t]);

  const handleContactClick = () => {
    // Scroll to the form section
    return navigate(`/${lang}/register`);
  }

  // Separate packages by payment interval
  const monthlyPackage = packages.find((pkg: Package) => pkg.paymentInterval === 'MONTHLY');
  const yearlyPackage = packages.find((pkg: Package) => pkg.paymentInterval === 'YEARLY');

  // Get the selected package based on interval
  const selectedPackage = selectedInterval === 'MONTHLY' ? monthlyPackage : yearlyPackage;

  // Calculate savings if both packages exist
  const calculateSavings = () => {
    if (monthlyPackage && yearlyPackage) {
      const monthlyYearlyTotal = monthlyPackage.price * 12;
      const savings = monthlyYearlyTotal - yearlyPackage.price;
      const savingsPercentage = ((savings / monthlyYearlyTotal) * 100).toFixed(0);
      return {
        amount: savings.toFixed(0),
        percentage: savingsPercentage
      };
    }
    return null;
  };

  const savings = calculateSavings();

  const plan = selectedPackage ? {
    _id: selectedPackage._id,
    name: selectedPackage.name,
    price: `$${selectedPackage.price}`,
    period: selectedInterval === 'MONTHLY' ? t('pricing.monthly') : t('pricing.yearly'),
    monthlyPrice: monthlyPackage ? `$${monthlyPackage.price}` : null,
    yearlyPrice: yearlyPackage ? `$${yearlyPackage.price}` : null,
    yearlyDiscount: savings ? `${t('pricing.yearlyDiscount')}` : t('pricing.yearlyDiscount'),
    yearlySavings: savings ? `$${savings.amount} ${t('pricing.yearlySavings')}` : null,
    description: selectedPackage.description || "Gelişmiş AI analitik çözümü",
    icon: faCrown,
    gradient: "from-purple-500 to-purple-700",
    borderColor: "border-purple-400/20",
    features: selectedPackage.features || [],
    cta: t('pricing.register'),
    productReferenceCode: selectedPackage.productReferenceCode,
    planReferenceCode: selectedPackage.planReferenceCode
  } : {
    _id: '',
    name: "Winnomi AI Premium",
    price: selectedInterval === 'MONTHLY' ? "$149" : "$1,609",
    period: selectedInterval === 'MONTHLY' ? t('pricing.monthly') : t('pricing.yearly'),
    monthlyPrice: "$149",
    yearlyPrice: "$1,609",
    yearlyDiscount: t('pricing.yearlyDiscount'),
    yearlySavings: `$179 ${t('pricing.yearlySavings')}`,
    description: "Gelişmiş AI analitik çözümü",
    icon: faCrown,
    gradient: "from-purple-500 to-purple-700",
    borderColor: "border-purple-400/20",
    features: [
      "AI performans analizi",
      "AI duygu durumu analizi",
      "Detaylı analitik veri izlenmesi ve takibi",
      "Günlük raporlama",
      "AI durum tespiti ve bildirimleri",
      "AI durum iyileştirme önerileri",
      "Modül analizleri (Analitik, Kitle, Etkileşim, AI Öngörüleri, Öneriler ve Hedefler)",
      "4 Sosyal medya hesabı entegrasyonu",
      "50 Kredilik Kullanım",
      "Müşteri destek"
    ],
    cta: t('pricing.register'),
    productReferenceCode: undefined,
    planReferenceCode: undefined
  };

  const benefits = [
    {
      icon: faChartLine,
      title: t('pricing.benefits.growth.title'),
      description: t('pricing.benefits.growth.description')
    },
    {
      icon: faBrain,
      title: t('pricing.benefits.aiAnalysis.title'),
      description: t('pricing.benefits.aiAnalysis.description')
    },
    {
      icon: faShieldAlt,
      title: t('pricing.benefits.reliable.title'),
      description: t('pricing.benefits.reliable.description')
    },
    {
      icon: faRocket,
      title: t('pricing.benefits.quickSetup.title'),
      description: t('pricing.benefits.quickSetup.description')
    }
  ];

  return (
    <section className="pt-12 sm:pt-16 md:pt-20">
      <div className="max-w-7xl mx-auto px-4 sm:px-6">
        {/* Section Header */}
        <div className="text-center mb-8 sm:mb-10">
          <h2 className="text-3xl sm:text-4xl md:text-5xl font-bold text-white mb-4 sm:mb-6 px-4">
            <span>
              {t('pricing.title')}
            </span>
            <GradientText className="block text-6xl sm:text-8xl md:text-10xl font-bold mt-2" text={t('pricing.titleHighlight')} />
          </h2>
          <p
            className="text-base sm:text-lg md:text-xl text-white/70 max-w-3xl mx-auto px-4"
          // style={{
          //   orphans: 4,
          //   widows: 4,
          //   textWrap: 'pretty'
          // }}
          >
            {t('pricing.subtitle')}
          </p>
        </div>

        {/* Single Wide Pricing Card */}
        {loading ? (
          <div className="max-w-6xl mx-auto mb-12 sm:mb-16 flex items-center justify-center py-20">
            <div className="text-center">
              <FontAwesomeIcon icon={faSpinner} className="text-purple-400 text-4xl animate-spin mb-4" />
              <p className="text-white/70">{t('pricing.loading')}</p>
            </div>
          </div>
        ) : error ? (
          <div className="max-w-6xl mx-auto mb-12 sm:mb-16">
            <div className="backdrop-blur-xl bg-red-500/10 border border-red-500/20 rounded-2xl sm:rounded-3xl p-6 sm:p-8 text-center">
              <p className="text-red-400">{error}</p>
            </div>
          </div>
        ) : (
          <div className="max-w-6xl mx-auto mb-12 sm:mb-16">
            {/* Interval Toggle - Only show if both packages exist */}
            {monthlyPackage && yearlyPackage && (
              <div className="flex justify-center mb-6 sm:mb-8">
                <div className="inline-flex items-center bg-white/5 border border-white/10 rounded-xl p-1">
                  <button
                    onClick={() => setSelectedInterval('MONTHLY')}
                    className={`px-6 sm:px-8 py-2 sm:py-3 rounded-lg font-semibold text-sm sm:text-base transition-all duration-300 ${selectedInterval === 'MONTHLY'
                      ? 'bg-gradient-to-r from-purple-500 to-purple-700 text-white shadow-lg shadow-purple-500/25'
                      : 'text-white/60 hover:text-white'
                      }`}
                  >
                    {t('pricing.monthly').replace('/', '')}
                  </button>
                  <button
                    onClick={() => setSelectedInterval('YEARLY')}
                    className={`px-6 sm:px-8 py-2 sm:py-3 rounded-lg font-semibold text-sm sm:text-base transition-all duration-300 relative ${selectedInterval === 'YEARLY'
                      ? 'bg-gradient-to-r from-purple-500 to-purple-700 text-white shadow-lg shadow-purple-500/25'
                      : 'text-white/60 hover:text-white'
                      }`}
                  >
                    {t('pricing.yearly').replace('/', '')}
                    {savings && (
                      <span className="absolute -top-2 -right-2 bg-green-500 text-white text-xs font-bold px-2 py-0.5 rounded-full">
                        -{savings.percentage}%
                      </span>
                    )}
                  </button>
                </div>
              </div>
            )}

            <div
              className={`relative backdrop-blur-xl bg-white/5 border ${plan.borderColor} rounded-2xl sm:rounded-3xl p-6 sm:p-8 md:p-12 md:hover:bg-white/10 transition-all duration-300 transform md:hover:scale-[1.01] md:active:scale-95 touch-manipulation ring-2 ring-purple-400/50 shadow-2xl shadow-purple-500/20`}
              style={{
                WebkitTapHighlightColor: 'transparent',
                WebkitTouchCallout: 'none',
                WebkitUserSelect: 'none',
                userSelect: 'none'
              }}
            >
              <div className="flex flex-col lg:flex-row gap-6 sm:gap-8 lg:gap-12">
                {/* Sol Taraf - Fiyat Bilgileri */}
                <div className="flex-1 flex flex-col justify-center">
                  <div className="text-center lg:text-left">
                    <div className={`w-12 h-12 sm:w-14 sm:h-14 md:w-16 md:h-16 bg-gradient-to-br ${plan.gradient} rounded-xl sm:rounded-2xl flex items-center justify-center mx-auto lg:mx-0 mb-4`}>
                      <FontAwesomeIcon icon={plan.icon} className="text-white text-xl sm:text-2xl" />
                    </div>

                    <h3 className="text-2xl sm:text-3xl lg:text-4xl font-bold text-white mb-3">
                      {plan.name}
                    </h3>

                    <p className="text-white/60 text-sm sm:text-base lg:text-lg mb-4 sm:mb-6">
                      {plan.description}
                    </p>

                    <div className="flex items-baseline justify-center lg:justify-start mb-4">
                      <span className="text-4xl sm:text-5xl lg:text-6xl font-bold text-white">
                        {plan.price}
                      </span>
                      <span className="text-white/60 ml-2 text-lg sm:text-xl">
                        {plan.period}
                      </span>
                    </div>

                    {/* Alternative Pricing Display */}
                    {selectedInterval === 'MONTHLY' && yearlyPackage && savings && (
                      <div className="mt-4 p-3 sm:p-4 bg-white/5 rounded-lg border border-white/10">
                        <div className="text-center lg:text-left">
                          <div className="text-purple-400 text-xs sm:text-sm font-medium mb-2">
                            {plan.yearlyDiscount}
                          </div>
                          <div className="flex items-baseline justify-center lg:justify-start">
                            <span className="text-2xl sm:text-3xl lg:text-4xl font-bold text-green-400">
                              {plan.yearlyPrice}
                            </span>
                            <span className="text-green-400/70 ml-2 text-base sm:text-lg">
                              {t('pricing.yearly')}
                            </span>
                          </div>
                          {plan.yearlySavings && (
                            <div className="text-green-400 text-xs sm:text-sm font-medium mt-2">
                              {plan.yearlySavings}
                            </div>
                          )}
                        </div>
                      </div>
                    )}

                    {selectedInterval === 'YEARLY' && monthlyPackage && savings && (
                      <div className="mt-4 p-3 sm:p-4 bg-white/5 rounded-lg border border-white/10">
                        <div className="text-center lg:text-left">
                          {/* <div className="text-white/60 text-xs sm:text-sm font-medium mb-2">
                          {t('pricing.monthly')} seçeneği
                        </div> */}
                          <div className="flex items-baseline justify-center lg:justify-start">
                            <span className="text-2xl sm:text-3xl lg:text-4xl font-bold text-white/60 line-through">
                              ${monthlyPackage.price * 12}
                            </span>
                            <span className="text-white/40 ml-2 text-base sm:text-lg">
                              {t('pricing.yearly')}
                            </span>
                          </div>
                          {plan.yearlySavings && (
                            <div className="text-green-400 text-xs sm:text-sm font-medium mt-2">
                              {plan.yearlySavings}
                            </div>
                          )}
                        </div>
                      </div>
                    )}

                    {/* CTA Button */}
                    <button
                      onClick={handleContactClick}
                      className="w-full lg:w-auto mt-6 sm:mt-8 px-8 sm:px-12 md:px-20 py-3 sm:py-4 rounded-xl sm:rounded-2xl font-semibold text-base sm:text-lg transition-all duration-300 transform md:hover:scale-105 md:active:scale-95 touch-manipulation bg-gradient-to-r from-purple-500 to-purple-700 text-white md:hover:from-purple-600 md:hover:to-purple-800 md:hover:shadow-2xl md:hover:shadow-purple-500/25"
                      style={{
                        WebkitTapHighlightColor: 'transparent',
                        WebkitTouchCallout: 'none',
                        WebkitUserSelect: 'none',
                        userSelect: 'none'
                      }}
                    >
                      {plan.cta}
                    </button>
                  </div>
                </div>

                {/* Sağ Taraf - Paket İçerik Bilgileri */}
                <div className="flex-1 border-l-0 lg:border-l border-white/10 pl-0 lg:pl-8 pt-6 sm:pt-8 lg:pt-0">
                  <h4 className="text-xl sm:text-2xl font-bold text-white mb-4 sm:mb-6">
                    {t('pricing.packageContent')}
                  </h4>
                  <div className="space-y-3 sm:space-y-4">
                    {plan.features.map((feature, featureIndex) => (
                      <div key={featureIndex} className="flex items-start space-x-2 sm:space-x-3">
                        <div className="w-5 h-5 sm:w-6 sm:h-6 bg-green-400 rounded-full flex items-center justify-center flex-shrink-0 mt-0.5">
                          <FontAwesomeIcon icon={faCheck} className="text-black text-xs" />
                        </div>
                        <span className="text-white/80 text-sm sm:text-base lg:text-lg">
                          {feature}
                        </span>
                      </div>
                    ))}
                  </div>
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Benefits Section */}
        <div className="backdrop-blur-xl bg-white/5 border border-white/10 rounded-2xl sm:rounded-3xl p-6 sm:p-8 mb-12 sm:mb-16">
          <div className="text-center mb-6 sm:mb-8">
            <h3 className="text-xl sm:text-2xl font-bold text-white mb-3 sm:mb-4">
              {t('pricing.includedInAllPlans')}
            </h3>
            <p
              className="text-white/70 text-sm sm:text-base px-4"
            // style={{
            //   orphans: 4,
            //   widows: 4,
            //   textWrap: 'pretty'
            // }}
            >
              {t('pricing.includedDescription')}
            </p>
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 sm:gap-6">
            {benefits.map((benefit, index) => (
              <div key={index} className="text-center p-4 sm:p-6 bg-white/5 rounded-xl border border-white/10 md:hover:bg-white/10 transition-all duration-300">
                <FontAwesomeIcon icon={benefit.icon} className="text-purple-400 text-2xl sm:text-3xl mb-3 sm:mb-4" />
                <h4 className="text-white font-semibold mb-2 text-sm sm:text-base">
                  {benefit.title}
                </h4>
                <p className="text-white/60 text-xs sm:text-sm">
                  {benefit.description}
                </p>
              </div>
            ))}
          </div>
        </div>

        {/* Bottom CTA */}
        {/* <div className="text-center">
          <div className="backdrop-blur-xl bg-white/5 border border-white/10 rounded-3xl p-8 max-w-4xl mx-auto">
            <h3 className="text-2xl font-bold text-white mb-4">
              Karar Veremediniz mi?
            </h3>
            <p 
              className="text-white/70 mb-6 max-w-2xl mx-auto"
              style={{
                orphans: 4,
                widows: 4,
                textWrap: 'pretty'
              }}
            >
              14 gün ücretsiz deneme ile tüm özelliklerimizi keşfedin. 
              Kredi kartı bilgisi gerektirmez, istediğiniz zaman iptal edebilirsiniz.
            </p>
            
            <div className="flex flex-col sm:flex-row items-center justify-center space-y-4 sm:space-y-0 sm:space-x-6">
              <button className="group bg-gradient-to-r from-purple-500 to-purple-700 px-8 py-4 rounded-2xl text-white font-semibold text-lg md:hover:from-purple-600 md:hover:to-purple-800 transition-all duration-300 transform md:hover:scale-105 md:hover:shadow-2xl md:hover:shadow-purple-500/25">
                <span className="flex items-center space-x-2">
                  <span>Denemeye Başla</span>
                  <FontAwesomeIcon icon={faArrowRight} className="md:group-hover:translate-x-1 transition-transform" />
                </span>
              </button>
              
              <div className="text-white/60 text-sm">
                <span className="font-semibold">14 gün ücretsiz</span> • Kredi kartı gerekmez
              </div>
            </div>
          </div>
        </div> */}
      </div>
    </section>
  );
};

export default Pricing;
